import type {
  AgeBand,
  AnyEvent,
  Child,
  ChildState,
  EventType,
  Mode,
  PayloadOf,
} from '@disdis/shared'
import { localDateOf } from '@disdis/shared'
import { log, reason } from '../app/log.js'
import { db } from '../state/db.js'
import { appendEvent, loadEvents } from '../state/events.js'
import { foldEvents, useChildStore } from '../state/store.js'
import { pullEvents, type SyncStatus, scheduleSync } from '../state/sync.js'
import { me } from './api.js'
import { patchParentSession } from './session.js'

/**
 * The dashboard reads the same event log the child plays on: every child of this parent is
 * folded locally with `@disdis/shared`, so the numbers on the panel are the numbers in the
 * game and the screen still works offline (PAR-03).
 */

const logger = log('parent-children')

export type ParentChild = {
  id: string
  nickname: string
  ageBand: AgeBand
  mode: Mode
  avatarId: string
  petName: string
  reviewMode: boolean
  /** Null while the child has no event on this device yet. */
  state: ChildState | null
}

export type ParentChildren = {
  children: ParentChild[]
  sync: SyncStatus
}

async function localChildIds(): Promise<string[]> {
  const keys = await db.events.orderBy('childId').uniqueKeys()
  return keys.map((key) => String(key))
}

async function foldChild(id: string, server: Child | undefined): Promise<ParentChild> {
  const events = await loadEvents(id)
  const state = events.length > 0 ? await foldEvents(events, localDateOf()) : null
  const profile = state?.profile
  return {
    id,
    nickname: profile?.nickname ?? server?.nickname ?? '',
    ageBand: profile?.ageBand ?? server?.ageBand ?? '8-12',
    mode: profile?.mode ?? server?.mode ?? 'core',
    avatarId: profile?.avatarId ?? server?.avatarId ?? '',
    petName: profile?.petName ?? server?.petName ?? '',
    reviewMode: profile?.reviewMode ?? server?.reviewMode ?? false,
    state,
  }
}

/**
 * Asks the server who the children are and pulls their events, then folds whatever the device
 * has. A server that cannot be reached only costs the pull: the fold still runs.
 */
export async function loadParentChildren(): Promise<ParentChildren> {
  let server: Child[] | null = null
  let sync: SyncStatus = 'synced'
  try {
    const account = await me()
    server = account.children
    await patchParentSession({
      researchConsent: account.parent.researchConsent,
      emailVerified: account.parent.emailVerified,
    })
    for (const child of account.children) {
      try {
        await pullEvents(child.id)
      } catch (error) {
        sync = 'error'
        logger.warn(`could not pull ${child.id}`, reason(error))
      }
    }
  } catch (error) {
    sync = 'error'
    logger.warn('the account could not be read, folding what the device has', reason(error))
  }

  const ids = new Set<string>(await localChildIds())
  for (const child of server ?? []) ids.add(child.id)
  const byId = new Map((server ?? []).map((child) => [child.id, child]))

  const children: ParentChild[] = []
  for (const id of ids) children.push(await foldChild(id, byId.get(id)))
  children.sort((a, b) => a.nickname.localeCompare(b.nickname, 'tr'))
  return { children, sync }
}

/**
 * Parent authored events (an observation, a mode switch) are appended for a named child, which
 * may not be the one on the phone. CONTRACTS §2: the appending screen owns its event type.
 */
export async function appendParentEvent<T extends EventType>(
  childId: string,
  type: T,
  payload: PayloadOf<T>,
  reviewMode: boolean,
): Promise<AnyEvent | null> {
  const event = await appendEvent(type, payload, { childId, reviewMode })
  if (!event) return null
  scheduleSync(childId)
  if (useChildStore.getState().childId === childId) await useChildStore.getState().refresh()
  return event
}

export type BrushRecord = {
  ts: number
  window: 'morning' | 'evening' | 'outside'
  outcome: 'verified' | 'partial' | 'tried' | 'nocam'
  activeSeconds: number
  sessionSeconds: number
}

/**
 * The calendar's day detail wants the times and the active seconds, which the fold does not
 * keep, so the screen reads the `brush_session` events for that child directly.
 */
export async function loadBrushDays(childId: string): Promise<Map<string, BrushRecord[]>> {
  const events = await loadEvents(childId)
  const byDate = new Map<string, BrushRecord[]>()
  for (const event of events) {
    if (event.type !== 'brush_session') continue
    const day = byDate.get(event.localDate) ?? []
    day.push({
      ts: event.ts,
      window: event.payload.window,
      outcome: event.payload.outcome,
      activeSeconds: event.payload.activeSeconds,
      sessionSeconds: event.payload.sessionSeconds,
    })
    byDate.set(event.localDate, day)
  }
  for (const day of byDate.values()) day.sort((a, b) => a.ts - b.ts)
  return byDate
}
